import type { EpubJsBook } from '../types/epubjs';
import type { LoiItem } from '../types/navigation';

function findLoiNav(doc: XMLDocument): Element | null {
  const byAttr = doc.querySelector('nav[*|type="loi"]');
  if (byAttr) return byAttr;
  return (
    Array.from(doc.querySelectorAll('nav')).find(
      (n) =>
        n.getAttributeNS('http://www.idpf.org/2007/ops', 'type') === 'loi' || n.getAttribute('epub:type') === 'loi',
    ) ?? null
  );
}

function resolveImgPath(imgSrc: string, navPath: string): string {
  if (imgSrc.startsWith('/') || imgSrc.includes('://')) return imgSrc;
  const navDir = navPath.substring(0, navPath.lastIndexOf('/') + 1);
  return `${navDir}${imgSrc}`;
}

export async function parseLoi(book: EpubJsBook): Promise<LoiItem[]> {
  const navPath: string = (book.packaging as any)?.navPath;
  if (!navPath) return [];

  let xml: XMLDocument;
  try {
    xml = (await (book as any).load(navPath, 'xml')) as XMLDocument;
  } catch {
    return [];
  }

  const loiNav = findLoiNav(xml);
  if (!loiNav) return [];

  const items: LoiItem[] = [];
  for (const anchor of Array.from(loiNav.querySelectorAll('li > a'))) {
    const rawHref = anchor.getAttribute('href') ?? '';
    const [href] = rawHref.split('#');
    const img = anchor.querySelector('img');
    if (!img || !href) continue;

    const imgSrc = img.getAttribute('src') ?? '';
    const alt = img.getAttribute('alt') ?? '';
    const filename = imgSrc.split('/').pop() ?? '';
    const manifest: Record<string, { href: string }> = (book.packaging as any)?.manifest ?? {};
    const manifestItem = Object.values(manifest).find(
      (item) => item.href === filename || item.href.endsWith(`/${filename}`),
    );
    const absImgPath = manifestItem?.href ?? resolveImgPath(imgSrc, navPath);
    // book.resolve() mirrors what epub.js does internally before every archive/CDN fetch:
    // it prepends book.path.directory (e.g. "EPUB/") and then book.url.origin so the
    // path is absolute.  archive.getBlob() strips exactly one leading "/" to get the
    // zip-internal path, so we must pass the resolved path, not the OPF-relative one.
    const resolvedImgPath: string = (book as any).resolve?.(absImgPath) ?? absImgPath;

    // For archive-backed books (ArrayBuffer/File), createUrl returns an ObjectURL from
    // the zip.  For URL-based (CDN) books book.archive is absent and createUrl would
    // trigger an XHR fetch, which is blocked by CORS on the CDN — so skip it entirely
    // and let the browser load the resolved CDN URL directly via <img src>.
    let blobUrl = resolvedImgPath;
    if ((book as any).archive) {
      try {
        blobUrl = await (book.resources as any).createUrl(resolvedImgPath);
      } catch {
        /* keep resolvedImgPath */
      }
    }

    items.push({ href, rawHref, src: blobUrl, alt, spineIndex: -1, isSpreadPairLeft: false, isSpreadPairRight: false });
  }
  return items;
}
